import { useState, useEffect } from "react";
import {
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiPlus,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";
import { showErrorToast } from "../Toast";
import ENDPOINTS from "../../api/endpoint";

export default function NewChatbotModal({
  isOpen,
  onClose,
  editingChatbotId = null,
  chatbotData = null,
  onSubmit,
  loading = false,
}) {
  const [newChatbotName, setNewChatbotName] = useState("");
  const [chatbotNamespace, setChatbotNamespace] = useState("");
  const [chatbotDescription, setChatbotDescription] = useState("");
  const [chatbotModel, setChatbotModel] = useState("gpt-5-mini");
  const [chatbotTone, setChatbotTone] = useState("professional");
  const [chatbotResponseLength, setChatbotResponseLength] = useState("medium");
  const [chatbotSystemPrompt, setChatbotSystemPrompt] = useState("");
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [temperature, setTemperature] = useState("");
  const [chunkSize, setChunkSize] = useState("");
  const [chunkOverlap, setChunkOverlap] = useState("");
  const [showNormalizationDictionary, setShowNormalizationDictionary] =
    useState(false);
  const [normalizationDictionary, setNormalizationDictionary] = useState([
    { key: "", value: "" },
  ]);

  // Models this company is allowed to use (set by the master admin)
  const [availableModels, setAvailableModels] = useState([
    { id: "gpt-5-mini", label: "GPT-5 Mini" },
  ]);

  // User access control
  const [showUserAccess, setShowUserAccess] = useState(false);
  const [restrictAccess, setRestrictAccess] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Load the company's endusers (for the access checklist) whenever the modal opens
  useEffect(() => {
    const loadCompanyUsers = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        if (!user.company_id) return;
        const token = user.access_token || localStorage.getItem("token");
        const res = await fetch(ENDPOINTS.GET_COMPANY_USERS(user.company_id), {
          headers: {
            Accept: "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (res.ok) {
          const data = await res.json();
          setCompanyUsers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        // Non-fatal; the access section will just show no users
      }
    };
    if (isOpen) {
      loadCompanyUsers();
    }
  }, [isOpen]);

  // When editing, load the chatbot's current access settings
  useEffect(() => {
    const loadAccess = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        if (!user.company_id || !editingChatbotId) return;
        const token = user.access_token || localStorage.getItem("token");
        const userId = user.id || user.user_id;
        setLoadingAccess(true);
        const res = await fetch(
          ENDPOINTS.GET_CHATBOT_ACCESS(user.company_id, editingChatbotId),
          {
            headers: {
              Accept: "application/json",
              ...(userId && { "X-User-Id": userId }),
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setRestrictAccess(Boolean(data.is_restricted));
          setSelectedUserIds((data.users || []).map((u) => u.id));
          if (data.is_restricted) {
            setShowUserAccess(true);
          }
        }
      } catch (err) {
        // Non-fatal
      } finally {
        setLoadingAccess(false);
      }
    };
    if (isOpen && editingChatbotId) {
      loadAccess();
    }
  }, [isOpen, editingChatbotId]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  useEffect(() => {
    const loadAllowedModels = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        if (!user.company_id) return;
        const token = localStorage.getItem("token");
        const res = await fetch(ENDPOINTS.GET_ALLOWED_MODELS(user.company_id), {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.allowed_models) && data.allowed_models.length > 0) {
            setAvailableModels(data.allowed_models);
            // For new chatbots, make sure the preselected model is one the
            // company is actually allowed to use
            if (!editingChatbotId) {
              setChatbotModel((prev) =>
                data.allowed_models.some((m) => m.id === prev)
                  ? prev
                  : data.allowed_models[0].id
              );
            }
          }
        }
      } catch (err) {
        // Keep fallback list if the request fails
      }
    };
    if (isOpen) {
      loadAllowedModels();
    }
  }, [isOpen, editingChatbotId]);

  const slugify = (value = "") =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  // Reset form function
  const resetForm = () => {
    setNewChatbotName("");
    setChatbotNamespace("");
    setChatbotDescription("");
    setChatbotModel("gpt-5-mini");
    setChatbotTone("professional");
    setChatbotResponseLength("medium");
    setChatbotSystemPrompt("");
    setShowAdvancedSettings(false);
    setTemperature("");
    setChunkSize("");
    setChunkOverlap("");
    setShowNormalizationDictionary(false);
    setNormalizationDictionary([{ key: "", value: "" }]);
    setShowUserAccess(false);
    setRestrictAccess(false);
    setSelectedUserIds([]);
  };

  // Populate form when editing
  useEffect(() => {
    if (isOpen && editingChatbotId && chatbotData) {
      setNewChatbotName(chatbotData.name || "");
      setChatbotNamespace(chatbotData.namespace || "");
      setChatbotDescription(chatbotData.description || "");
      setChatbotModel(chatbotData.llm_settings?.model || "gpt-5-mini");
      setChatbotTone(chatbotData.llm_settings?.tone || "professional");
      setChatbotResponseLength(
        chatbotData.llm_settings?.response_length || "medium"
      );
      setChatbotSystemPrompt(chatbotData.llm_settings?.system_prompt || "");
      // Advanced settings
      setTemperature(
        chatbotData.llm_settings?.temperature !== undefined
          ? String(chatbotData.llm_settings.temperature)
          : ""
      );
      setChunkSize(
        chatbotData.vector_settings?.chunk_size !== undefined
          ? String(chatbotData.vector_settings.chunk_size)
          : ""
      );
      setChunkOverlap(
        chatbotData.vector_settings?.chunk_overlap !== undefined
          ? String(chatbotData.vector_settings.chunk_overlap)
          : ""
      );
      // Show advanced settings if any of these values exist
      if (
        chatbotData.llm_settings?.temperature !== undefined ||
        chatbotData.vector_settings?.chunk_size !== undefined ||
        chatbotData.vector_settings?.chunk_overlap !== undefined
      ) {
        setShowAdvancedSettings(true);
      }
      // Load normalization dictionary if it exists
      if (
        chatbotData.normalization_dictionary &&
        typeof chatbotData.normalization_dictionary === "object"
      ) {
        const dictEntries = Object.entries(
          chatbotData.normalization_dictionary
        ).map(([key, value]) => ({ key: String(key), value: String(value) }));
        if (dictEntries.length > 0) {
          setNormalizationDictionary(dictEntries);
          setShowNormalizationDictionary(true);
        } else {
          // Empty object - initialize with one empty entry but don't show section
          setNormalizationDictionary([{ key: "", value: "" }]);
          setShowNormalizationDictionary(false);
        }
      } else {
        // No normalization_dictionary - initialize with one empty entry
        setNormalizationDictionary([{ key: "", value: "" }]);
        setShowNormalizationDictionary(false);
      }
    } else if (isOpen && !editingChatbotId) {
      // Reset form when opening for new chatbot
      resetForm();
    }
  }, [isOpen, editingChatbotId, chatbotData]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle normalization dictionary changes
  const handleDictionaryChange = (index, field, value) => {
    const updated = [...normalizationDictionary];
    updated[index][field] = value;
    setNormalizationDictionary(updated);
  };

  const handleAddDictionaryEntry = () => {
    setNormalizationDictionary([
      ...normalizationDictionary,
      { key: "", value: "" },
    ]);
  };

  const handleRemoveDictionaryEntry = (index) => {
    if (normalizationDictionary.length > 1) {
      const updated = normalizationDictionary.filter((_, i) => i !== index);
      setNormalizationDictionary(updated);
    }
  };

  const handleSubmit = () => {
    const name = newChatbotName.trim();
    if (!name) {
      return;
    }

    // Validate that name contains only alphabets, numbers, and spaces (no special characters)
    if (!/^[a-zA-Z0-9\s]+$/.test(name)) {
      return;
    }

    // Validate advanced settings if provided
    if (temperature.trim()) {
      const tempValue = parseFloat(temperature);
      if (isNaN(tempValue) || tempValue < 0 || tempValue > 1) {
        showErrorToast("Temperature must be between 0 and 1");
        return;
      }
    }

    if (chunkSize.trim()) {
      const chunkSizeValue = parseInt(chunkSize);
      if (
        isNaN(chunkSizeValue) ||
        chunkSizeValue < 1000 ||
        chunkSizeValue > 2000
      ) {
        showErrorToast("Chunk size must be between 1000 and 2000");
        return;
      }
    }

    if (chunkOverlap.trim()) {
      const chunkOverlapValue = parseInt(chunkOverlap);
      if (
        isNaN(chunkOverlapValue) ||
        chunkOverlapValue < 100 ||
        chunkOverlapValue > 200
      ) {
        showErrorToast("Chunk overlap must be between 100 and 200");
        return;
      }
    }

    const derivedNamespace = slugify(name);
    // During update, use existing namespace; during create, allow new namespace
    const finalNamespace = editingChatbotId
      ? chatbotNamespace.trim() // Use existing namespace during update
      : chatbotNamespace.trim() || derivedNamespace || `chatbot-${Date.now()}`;
    const description = chatbotDescription.trim();
    const systemPrompt =
      chatbotSystemPrompt.trim() || "You are a helpful assistant.";

    // Build llm_settings object
    const llmSettings = {
      system_prompt: systemPrompt,
      response_length: chatbotResponseLength,
      tone: chatbotTone,
      model: chatbotModel,
    };

    // Add temperature if provided and valid
    if (temperature.trim()) {
      const tempValue = parseFloat(temperature);
      if (!isNaN(tempValue) && tempValue >= 0 && tempValue <= 1) {
        llmSettings.temperature = tempValue;
      }
    }

    // Build vector_settings object if chunk_size or chunk_overlap are provided
    const vectorSettings = {};
    if (chunkSize.trim()) {
      const chunkSizeValue = parseInt(chunkSize);
      if (
        !isNaN(chunkSizeValue) &&
        chunkSizeValue >= 1000 &&
        chunkSizeValue <= 2000
      ) {
        vectorSettings.chunk_size = chunkSizeValue;
      }
    }
    if (chunkOverlap.trim()) {
      const chunkOverlapValue = parseInt(chunkOverlap);
      if (
        !isNaN(chunkOverlapValue) &&
        chunkOverlapValue >= 100 &&
        chunkOverlapValue <= 200
      ) {
        vectorSettings.chunk_overlap = chunkOverlapValue;
      }
    }

    const payload = {
      name,
      namespace: finalNamespace,
      description,
      llm_settings: llmSettings,
      is_default: false,
    };

    // Only include vector_settings if it has at least one property
    if (Object.keys(vectorSettings).length > 0) {
      payload.vector_settings = vectorSettings;
    }

    // Build normalization_dictionary object from array
    const normalizationDict = {};
    normalizationDictionary.forEach((entry) => {
      const key = entry.key.trim();
      const value = entry.value.trim();
      if (key && value) {
        normalizationDict[key] = value;
      }
    });

    // Only include normalization_dictionary if it has at least one entry
    if (Object.keys(normalizationDict).length > 0) {
      payload.normalization_dictionary = normalizationDict;
    }

    // Validate access settings: restricting to nobody would lock everyone out
    if (restrictAccess && selectedUserIds.length === 0) {
      showErrorToast(
        "Select at least one user, or turn off restricted access"
      );
      return;
    }

    onSubmit(payload, {
      is_restricted: restrictAccess,
      user_ids: restrictAccess ? selectedUserIds : [],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full min-w-md max-w-lg max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editingChatbotId ? "Update Chatbot" : "Create Chatbot"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                Chatbot Name
              </label>
              <input
                type="text"
                value={newChatbotName}
                onChange={(e) => {
                  // Only allow alphabets, numbers, and spaces (no special characters)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, "");
                  setNewChatbotName(value);
                  // Only update namespace if not in edit mode
                  if (!editingChatbotId) {
                    setChatbotNamespace(slugify(value));
                  }
                }}
                placeholder="e.g. Legal Assistant"
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                Namespace
              </label>
              <input
                readOnly={!!editingChatbotId}
                type="text"
                value={chatbotNamespace}
                onChange={(e) => {
                  // Only allow changes if not in edit mode
                  if (!editingChatbotId) {
                    setChatbotNamespace(slugify(e.target.value));
                  }
                }}
                placeholder="auto-generated"
                className={`w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  editingChatbotId
                    ? "bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                    : ""
                }`}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={chatbotDescription}
                onChange={(e) => setChatbotDescription(e.target.value)}
                placeholder="What is this chatbot for?"
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                Model
              </label>
              <div className="relative">
                <select
                  value={chatbotModel}
                  onChange={(e) => setChatbotModel(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
                >
                  {/* Grandfathered: keep showing the current model even if no longer allowed */}
                  {(availableModels.some((m) => m.id === chatbotModel)
                    ? availableModels
                    : [{ id: chatbotModel, label: chatbotModel }, ...availableModels]
                  ).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} ({m.id})
                    </option>
                  ))}
                </select>
                <FiChevronDown
                  size={18}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                System Prompt
              </label>
              <textarea
                value={chatbotSystemPrompt}
                onChange={(e) => setChatbotSystemPrompt(e.target.value)}
                placeholder="You are a helpful assistant."
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Response length
                </label>
                <div className="relative">
                  <select
                    value={chatbotResponseLength}
                    onChange={(e) => setChatbotResponseLength(e.target.value)}
                    className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
                  >
                    <option value="short">short</option>
                    <option value="medium">medium</option>
                    <option value="long">long</option>
                  </select>
                  <FiChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Tone
                </label>
                <div className="relative">
                  <select
                    value={chatbotTone}
                    onChange={(e) => setChatbotTone(e.target.value)}
                    className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
                  >
                    <option value="professional">professional</option>
                    <option value="encouraging">encouraging</option>
                    <option value="persuasive">persuasive</option>
                    <option value="neutral">neutral</option>
                    <option value="empathetic">empathetic</option>
                    <option value="authoritative">authoritative</option>
                    <option value="reflective">reflective</option>
                    <option value="sympathetic">sympathetic</option>
                  </select>
                  <FiChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Settings Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-2 px-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span>Advanced Settings</span>
                {showAdvancedSettings ? (
                  <FiChevronUp
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                ) : (
                  <FiChevronDown
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                )}
              </button>

              {/* Advanced Settings Fields */}
              {showAdvancedSettings && (
                <div className="mt-4 space-y-4 pl-2  dark:border-gray-700">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Temperature{" "}
                      <span className="text-xs text-gray-500">(0-1)</span>
                    </label>
                    <input
                      type="text"
                      value={temperature}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, numbers, and single decimal point
                        if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                          // Limit to 3 digits after decimal point
                          const parts = value.split(".");
                          if (parts.length === 2 && parts[1].length > 1) {
                            // If more than 3 decimal places, truncate to 3
                            setTemperature(
                              parts[0] + "." + parts[1].substring(0, 1)
                            );
                          } else {
                            setTemperature(value);
                          }
                        }
                      }}
                      placeholder="e.g. 0.7"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {temperature &&
                      (parseFloat(temperature) < 0 ||
                        parseFloat(temperature) > 1) && (
                        <p className="text-xs text-red-500 mt-1">
                          Temperature must be between 0 and 1
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Chunk Size{" "}
                      <span className="text-xs text-gray-500">(1000-2000)</span>
                    </label>
                    <input
                      type="text"
                      value={chunkSize}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers (allow any numeric input while typing)
                        if (value === "" || /^[0-9]+$/.test(value)) {
                          setChunkSize(value);
                        }
                      }}
                      placeholder="e.g. 1200"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {chunkSize &&
                      (parseInt(chunkSize) < 1000 ||
                        parseInt(chunkSize) > 2000) && (
                        <p className="text-xs text-red-500 mt-1">
                          Chunk size must be between 1000 and 2000
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Chunk Overlap{" "}
                      <span className="text-xs text-gray-500">(100-200)</span>
                    </label>
                    <input
                      type="text"
                      value={chunkOverlap}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers (allow any numeric input while typing)
                        if (value === "" || /^[0-9]+$/.test(value)) {
                          setChunkOverlap(value);
                        }
                      }}
                      placeholder="e.g. 150"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {chunkOverlap &&
                      (parseInt(chunkOverlap) < 100 ||
                        parseInt(chunkOverlap) > 200) && (
                        <p className="text-xs text-red-500 mt-1">
                          Chunk overlap must be between 100 and 200
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Name Normalization Dictionary Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() =>
                  setShowNormalizationDictionary(!showNormalizationDictionary)
                }
                className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-2 px-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span>Name Normalization Dictionary</span>
                {showNormalizationDictionary ? (
                  <FiChevronUp
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                ) : (
                  <FiChevronDown
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                )}
              </button>

              {/* Normalization Dictionary Fields */}
              {showNormalizationDictionary && (
                <div className="mt-4 space-y-3 pl-2">
                  {normalizationDictionary.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={entry.key}
                          onChange={(e) =>
                            handleDictionaryChange(index, "key", e.target.value)
                          }
                          placeholder="IT"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="text"
                          value={entry.value}
                          onChange={(e) =>
                            handleDictionaryChange(
                              index,
                              "value",
                              e.target.value
                            )
                          }
                          placeholder="Information Technology"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {normalizationDictionary.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveDictionaryEntry(index)}
                          className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title="Remove entry"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddDictionaryEntry}
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors text-sm"
                    >
                      <FiPlus size={16} />
                      <span>Add Entry</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Access Section */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowUserAccess(!showUserAccess)}
                className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-2 px-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <FiUsers size={16} />
                  User Access
                  {restrictAccess && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                      Restricted
                    </span>
                  )}
                </span>
                {showUserAccess ? (
                  <FiChevronUp
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                ) : (
                  <FiChevronDown
                    size={18}
                    className="text-gray-600 dark:text-gray-400"
                  />
                )}
              </button>

              {showUserAccess && (
                <div className="mt-3 space-y-3 pl-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restrictAccess}
                      onChange={(e) => setRestrictAccess(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Restrict access to specific users
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    When off, everyone in your company can use this chatbot.
                    When on, only the users you check below (plus admins) can
                    see and use it.
                  </p>

                  {restrictAccess && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-md max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                      {loadingAccess ? (
                        <p className="text-sm text-gray-500 p-3">
                          Loading users...
                        </p>
                      ) : companyUsers.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3">
                          No users found for your company.
                        </p>
                      ) : (
                        companyUsers.map((u) => (
                          <label
                            key={u.id}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(u.id)}
                              onChange={() => toggleUserSelection(u.id)}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-800 dark:text-gray-200">
                              {u.name || u.email}
                              {u.name && (
                                <span className="text-xs text-gray-500 ml-2">
                                  {u.email}
                                </span>
                              )}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end gap-3 p-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            disabled={!newChatbotName.trim() || loading}
          >
            {editingChatbotId ? "Update Chatbot" : "Create Chatbot"}
          </button>
        </div>
      </div>
    </div>
  );
}
